import { ColorPalette, StarLarge } from '@griply/assets';
import { defaultTagColorOption } from '@griply/utils';
import React, { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Colors as ButtonColors,
  Sizes as ButtonSizes,
  Variants,
} from 'shared/components/ui/button';
import { ColorPickerFormSelect } from 'shared/components/ui/color-picker';
import { Size as SelectSize } from 'shared/components/ui/custom-form-select';
import { Divider } from 'shared/components/ui/divider';
import { Icon } from 'shared/components/ui/icon';
import {
  ResizableTextArea,
  Variants as ResizableTextAreaVariants,
} from 'shared/components/ui/resizable-text-area';
import { Toggle } from 'shared/components/ui/toggle';
import { TagFormFields } from 'shared/types/tag-form';

import * as Styled from './tag-form.style';
import { useTagForm } from './use-tag-form';

const defaultInitialValues: TagFormFields = {
  color: defaultTagColorOption.value,
  favorite: false,
};

export type TagFormProps = {
  initialValues?: TagFormFields;
  onSubmit: (tag: TagFormFields) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  onCancel: () => void;
  isEdit?: boolean;
};

export const TagForm: React.FC<TagFormProps> = ({
  initialValues = {},
  onSubmit,
  onDirtyChange,
  onCancel,
  isEdit,
}) => {
  const { t } = useTranslation();
  const {
    control,
    onSubmit: submitForm,
    isValid,
    isDirty,
    setValue,
    watch,
    setFocus,
  } = useTagForm(onSubmit, { ...defaultInitialValues, ...initialValues });

  const favorite = watch<'favorite'>('favorite');
  const onChangeFavorite = () =>
    setValue<'favorite'>('favorite', !favorite, { shouldDirty: true });

  const color = watch<'color'>('color');
  const onChangeColor = (color?: string) =>
    setValue<'color'>('color', color ?? defaultInitialValues.color, {
      shouldDirty: true,
    });

  useEffect(() => {
    if (!isEdit) {
      setFocus('name');
    }
  }, [isEdit, setFocus]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  return (
    <Styled.Form noValidate onSubmit={submitForm}>
      <Styled.ScrollArea>
        <Styled.Header>
          <Styled.TitleColumn>
            <Controller
              control={control}
              name="name"
              render={({ field }) => (
                <ResizableTextArea
                  ref={field.ref}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  variant={ResizableTextAreaVariants.Subtitle1}
                  placeholder={t('forms.tag.fields.name.placeholder')}
                  fullWidth
                />
              )}
            />
          </Styled.TitleColumn>
        </Styled.Header>

        <Styled.Body>
          <Divider />

          <Styled.Section>
            <Styled.Item>
              <Styled.Label>
                <Icon icon={ColorPalette} size={16} strokeWidth={1.5} />
                <Styled.LabelText>
                  {t('forms.tag.fields.color.label')}
                </Styled.LabelText>
              </Styled.Label>
              <ColorPickerFormSelect
                onChange={onChangeColor}
                color={color}
                size={SelectSize.ExtraSmall}
              />
            </Styled.Item>

            <Styled.Item $autoWidthField>
              <Styled.Label>
                <Icon icon={StarLarge} size={16} strokeWidth={1.5} />
                <Styled.LabelText>
                  {t('forms.tag.fields.favorite.label')}
                </Styled.LabelText>
              </Styled.Label>
              <Toggle
                value="favorite"
                checked={!!favorite}
                onChange={onChangeFavorite}
              />
            </Styled.Item>
          </Styled.Section>
        </Styled.Body>
      </Styled.ScrollArea>

      <Styled.Footer>
        <Button
          variant={Variants.Contained}
          size={ButtonSizes.Medium}
          color={ButtonColors.White}
          onClick={onCancel}
          type="button"
        >
          {t('forms.tag.buttons.cancel.label')}
        </Button>
        <Button size={ButtonSizes.Medium} type="submit" disabled={!isValid}>
          {t('forms.tag.buttons.save.label')}
        </Button>
      </Styled.Footer>
    </Styled.Form>
  );
};
